import React from 'react';
import PropTypes from 'prop-types';

import { 
  makeStyles 
} from '@material-ui/core/styles';

import { 
  Alert, 
  AlertTitle,
} from '@material-ui/lab';

import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';

import {
  Person as UserIcon,
  SupervisorAccount as AdminIcon,
  Policy as AuditorIcon,
} from '@material-ui/icons';

import { 
  fmtIso8601AsLocalDateHrsMin,
}                         from '../../utils/formatters';

import {
  DetailPanel,
} from '../../../packages/detail-panel';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  sections: {
    padding: theme.spacing(0),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'row',
  },
  section: {
    padding: theme.spacing(1),
    minWidth: 300,
    flexGrow: 1,
  },
  table: {
    minWidth: 400,
  }
}));

const AuthorizedClients = (props) => {

  const { dataLakeRole, clients } = props;
  const classes = useStyles();

  if(!clients) {  
    return(null);
  }

  const rows = Object.values(clients).sort((a, b) => {
    var nameA = a.ClientAccountAlias.toUpperCase();
    var nameB = b.ClientAccountAlias.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  
  return(
    <React.Fragment>
      {dataLakeRole==='Admin'?
        <Alert severity="warning">
          <AlertTitle>Data Lake Administrator</AlertTitle>
          You have full non-expiring console permissions in all client accounts 
          listed below irrespective of assigned roles and expirations listed below. 
        </Alert>:
       dataLakeRole==='Auditor'?
        <Alert severity="info">
          <AlertTitle>Data Lake Auditor</AlertTitle>
          You have read-only non-expiring console permissions in all client accounts
          listed below.  You will have elevated permissions in any clients where you 
          have been assigned a User or Admin role. 
        </Alert>:
        <Alert severity="info">
          <AlertTitle>Data Lake User</AlertTitle>
          Your access to the following client accounts is assigned automatically based 
          on your recent AWS console logins.  These authorizations will automatically expire.
          To establish or restore access to a data lake client simply login to it via the 
          AWS console then refresh this page.
        </Alert>
      }
      <TableContainer component={Paper}>
        <Table className={classes.table} size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Assigned Role</TableCell>
              <TableCell>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.ClientAccountId}>
                <TableCell component="th" scope="row">
                  {row.ClientAccountAlias}
                </TableCell>
                <TableCell>{row.AssignedRole}</TableCell>
                <TableCell>{fmtIso8601AsLocalDateHrsMin(row.AssignedRoleExpiration)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
  
};

export default function UserCard(props) {
  const { user } = props;
  const classes = useStyles();

  if (!user) return(null);

  const fields=[
    { section: "Identity", fields: [
      { name: "username", label: "Username" },
      { name: "amplifyData.signInUserSession.idToken.payload.pac_tenant_name", label: "Tenant Name" },
      { name: "fullname", label: "Full Name" },
      { name: "email", label: "E-Mail" },
      { name: "phone", label: "Phone" },
    ]},
    { section: "Authorizations", fields: [
      { name: "AuthorizedClients", label: "_", component: AuthorizedClients, 
        options: {
          dataLakeRole: user.role,
          clients: user.clients,
        }},
    ]},
  ];

  // gather unsectioned fields into a default section
  const sections=[];
  const unsectionedFields=[];
  fields.forEach(field => {
    if(field.section) {
      sections.push(field);
    } else {
      unsectionedFields.push(field);
    }
  });
  if(unsectionedFields.length) {
    sections.push({
      section: "", fields: unsectionedFields
    });
  }

  return (
    <Card className={classes.root}>
      <CardHeader 
        avatar={
          <Avatar aria-label="type" className={classes.avatar}>
            {user.role==='Admin'?<AdminIcon/>:
                user.role==='Auditor'?<AuditorIcon/>:
                  <UserIcon/>
            }
          </Avatar>
        }
        title={user.username}
        subheader={user.role}
      />
      <CardContent>
        <Grid container className={classes.sections}>
          {
            sections.map((section, index) => {
              return(
                <Grid key={"s"+index} item className={classes.section} >
                  <DetailPanel 
                    name={section.section} 
                    data={user}
                    fieldWidth='600px'
                    {...section}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </CardContent>
    </Card>
  );
}

UserCard.propTypes = {
  user: PropTypes.object,
};

UserCard.defaultProps = {
};
